import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBarModule, MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { ApiLinks } from '@cutting/app.api-links';
import { HubConnection } from '@microsoft/signalr';
import {
  CadModelMaterialType,
  CuttingClientMaterial,
  CuttingProduct,
  ICustomResponse,
  LegacyDialogsService,
  LoadingStateEnum,
  MaterialType,
  NgxLocalizedNumbers,
  PriceList,
  ProductModelViewerDialogComponent,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, Subject, merge, of } from 'rxjs';
import { catchError, concatMap, filter, finalize, flatMap, switchMap, takeUntil, tap, timeout } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { AsyncPipe, JsonPipe, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { ClientMaterialService } from '@cutting/main/shared/services/client-material.service';
import { OrderDetailService } from '../order-detail.service';
import { AddElasticModelDialogComponent } from './add-product-dialog/add-product-dialog.component';
import { EditProductDialogComponent } from './edit-product-dialog/edit-product-dialog/edit-product-dialog.component';
import { ElementsInfoDialogComponent } from './elements-info-dialog/elements-info-dialog.component';
import { ProductMaterialsLinkDialogComponent } from './product-materials-link-dialog/product-materials-link-dialog.component';

@Component({
  selector: 'cutting-products-list',
  templateUrl: './products-list.component.html',
  styleUrls: ['./products-list.component.scss'],
  standalone: true,
  imports: [
    NgSwitch,
    NgSwitchCase,
    NgIf,
    NgxLocalizedNumbers,
    FormsModule,
    JsonPipe,
    AsyncPipe,
    MatProgressSpinnerModule,
    MatTableModule,
    MatIconModule,
    MatCheckboxModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    MatLegacySnackBarModule,
    ProductModelViewerDialogComponent,
  ],
})
export class ElasticModelListComponent implements OnDestroy, OnChanges {
  @Input() editableOrder = false;

  @Input() hubConnection: HubConnection | undefined;

  @Output() needSaveOrder: EventEmitter<void> = new EventEmitter();

  data$: Observable<ICustomResponse<CuttingProduct[]>>;
  update$: Subject<void> = new Subject();
  unsubscribe$: Subject<void> = new Subject();
  priceLists$: Observable<ICustomResponse<PriceList[]>>;
  orderSaved$: Subject<boolean> = new Subject();
  productModelViewerDialog: MatDialogRef<ProductModelViewerDialogComponent>;

  addElasticModelDialog: MatDialogRef<AddElasticModelDialogComponent>;
  elasticModelElementsInfoDialog: MatDialogRef<ElementsInfoDialogComponent>;
  linkProductMaterialsDialogRef: MatDialogRef<ProductMaterialsLinkDialogComponent>;

  priceLists: PriceList[] = [];

  selectedProducts = new SelectionModel<number>(true, []);
  selectedAll = false;

  displayedColumns: string[] = [
    'select',
    'name',
    'article',
    'sign',
    'materials',
    'loadingState',
    'size',
    'count',
    'price',
    //'edit',
    'view',
    'delete',
  ];
  dataSource: MatTableDataSource<CuttingProduct> = new MatTableDataSource();

  get orderId(): number {
    return this.orderDetailService.order ? this.orderDetailService.order.id : 0;
  }

  get loader$(): Observable<ICustomResponse<CuttingProduct[]>> {
    return this.orderId <= 0 ? of({ error: void 0, result: [] }) : this.orderDetailService.getElasticProducts();
  }

  constructor(
    private orderDetailService: OrderDetailService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private dialogsService: LegacyDialogsService,
    private clientMaterialService: ClientMaterialService,
    private injector: Injector
  ) {
    this.data$ = merge(this.update$.pipe(switchMap(() => this.loader$)), this.loader$).pipe(
      tap(p => {
        this.dataSource.data = [...(p.result || [])];

        // add to client materials
        if (p.result) {
          p.result
            .filter(
              prod =>
                prod.loadingState === LoadingStateEnum.ProductMaterialReplaced ||
                prod.loadingState === LoadingStateEnum.Completed ||
                prod.loadingState === LoadingStateEnum.ProductMaterialLinked
            )
            .forEach(prod => {
              prod.parsedMaterials
                .filter(
                  m => m.materialType === CadModelMaterialType.Plate || m.materialType === CadModelMaterialType.Linear
                )
                .forEach(m => {
                  this.clientMaterialService.updateMaterials('product', {
                    article: m.linkedMaterialArticle,
                    name: m.linkedMaterialName,
                    inMaterialBaseId: m.linkedMaterialId,
                    type: m.materialType === CadModelMaterialType.Linear ? MaterialType.Linear : MaterialType.Plate,
                    preparedForDeletion: false,
                    cuttings: [],
                    id: 0,
                  } as CuttingClientMaterial);
                });
            });
        }
      })
    );

    this.priceLists$ = this.orderDetailService.getPriceLists().pipe(
      tap(res => {
        this.priceLists = res.result ? res.result : [];
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.hubConnection) {
      return;
    }

    const currentItem: SimpleChange = changes.hubConnection;

    if (currentItem?.previousValue !== currentItem?.currentValue && currentItem?.currentValue) {
      currentItem.currentValue.on('productMaterialsChanged', (status: number) => {
        if (status === 2) {
          this.snackBar.open($localize`Не удалось заменить материалы в изделии. Прайс-лист зашифрован`);
        } else if (status > 0) {
          this.snackBar.open($localize`Не удалось заменить материалы в изделии`);
        }

        this.update$.next();
      });

      currentItem.currentValue.on(
        'productMaterialsLinkReplaced',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (_orderId: number, _productId: number, state: number, _errors: string) => {
          if (state === 2) {
            this.snackBar.open($localize`Не удалось заменить материалы в изделии. Прайс-лист зашифрован`);
          } else if (state > 0) {
            this.snackBar.open($localize`Не удалось заменить материалы в изделии`);
          }

          this.update$.next();
        }
      );
    }
  }

  hasProducts(): boolean {
    if (!this.dataSource.data) {
      return false;
    }

    return (
      this.dataSource.data.filter(
        p =>
          p.loadingState === LoadingStateEnum.ProductMaterialReplaced || p.loadingState === LoadingStateEnum.Completed
      ).length > 0
    );
  }

  hasNoLinkedProducts(): boolean {
    if (!this.dataSource.data) {
      return false;
    }

    for (const p of this.dataSource.data) {
      if (
        p.loadingState !== LoadingStateEnum.ProductMaterialReplaced &&
        p.loadingState !== LoadingStateEnum.Completed
      ) {
        return true;
      }
    }

    return false;
  }

  openAddElasticModelDialog(): void {
    if (!this.priceLists || this.priceLists.length === 0) {
      this.snackBar.open($localize`Прайс-лист не загружен`);
      return;
    }

    let add$ = of(false);

    if (!this.orderId) {
      add$ = this.dialogsService.confirm($localize`Заказ будет сохранен. Продолжить?`).pipe(
        filter(res => res),
        tap(() => this.needSaveOrder.emit()),
        switchMap(() =>
          this.orderSaved$.pipe(
            timeout(15000),
            catchError(() => of(false))
          )
        ),
        filter(res => res)
      );
    }

    const parentInjector = this.injector;

    const createDialog = (): void => {
      this.addElasticModelDialog = this.dialog.open(AddElasticModelDialogComponent, {
        disableClose: false,
        panelClass: 'add-product-dialog',
        width: '95vw',
        height: '95vh',
        injector: Injector.create({
          providers: [{ provide: OrderDetailService, useValue: parentInjector.get(OrderDetailService) }],
          parent: parentInjector,
        }),
      });

      this.addElasticModelDialog.componentInstance.productNum = this.generateUniqueProductNumber(
        this.dataSource.data.map(p => p.sign)
      );
      this.addElasticModelDialog.componentInstance.priceLists = this.priceLists;
    };

    add$
      .pipe(
        tap(() => createDialog()),
        concatMap(() => this.addElasticModelDialog.afterClosed()),
        filter<boolean | undefined>(res => res),
        tap(() => {
          this.update$.next();
          this.orderDetailService.getOrder();
          this.orderDetailService.getOrderEstimate();
        }),
        finalize(() => (this.addElasticModelDialog = null))
      )
      .subscribe();
  }

  openElasticModelElementsInfoDialog(item: CuttingProduct): void {
    this.elasticModelElementsInfoDialog = this.dialog.open(ElementsInfoDialogComponent, {
      disableClose: false,
      panelClass: 'product-elements-info-dialog',
      width: '1200px',
    });

    this.elasticModelElementsInfoDialog.componentInstance.productProcessingMode = item.processingMode;
    this.elasticModelElementsInfoDialog.componentInstance.elements = item.elements;
    this.elasticModelElementsInfoDialog.componentInstance.multiplier = item.count;
    this.elasticModelElementsInfoDialog.componentInstance.productMaterialLink = item.parsedMaterials;

    this.elasticModelElementsInfoDialog
      .afterClosed()
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => (this.elasticModelElementsInfoDialog = null))
      )
      .subscribe();
  }

  selectAll(): void {
    this.selectedProducts.clear();
    this.selectedAll = !this.selectedAll;
    if (this.selectedAll && this.dataSource.data.length > 0) {
      this.selectedProducts.select(...this.dataSource.data.map(d => d.id));
    }
  }

  deleteCuttingProduct(product: CuttingProduct): void {
    this.orderDetailService
      .deleteCuttingProduct(product.id)
      .pipe(
        takeUntil(this.unsubscribe$),
        catchError(() => {
          this.snackBar.open($localize`Не удалось сохранить изделие`);
          return of(undefined);
        }),
        tap(() => {
          this.snackBar.open($localize`Изделие удалено`);
          this.update$.next();

          this.orderDetailService.getOrder();
          this.orderDetailService.getOrderEstimate();

          if (
            product.loadingState === LoadingStateEnum.ProductMaterialReplaced ||
            product.loadingState === LoadingStateEnum.Completed ||
            product.loadingState === LoadingStateEnum.ProductMaterialLinked
          ) {
            product.parsedMaterials
              .filter(
                m => m.materialType === CadModelMaterialType.Plate || m.materialType === CadModelMaterialType.Linear
              )
              .forEach(m => {
                this.clientMaterialService.prepareForDeletion('product', m.linkedMaterialName, m.linkedMaterialArticle);
              });
          }
        })
      )
      .subscribe();
  }

  deleteCuttingProducts(): void {
    if (this.selectedProducts.selected.length === 0) {
      this.snackBar.open($localize`Выберите изделия для удаления`);
      return;
    }

    this.dialogsService
      .confirm($localize`Удалить выбранные изделия?`)
      .pipe(
        takeUntil(this.unsubscribe$),
        concatMap(() => {
          return this.orderDetailService.deleteCuttingProducts(this.selectedProducts.selected);
        }),
        catchError(() => {
          this.snackBar.open($localize`Не удалось удалить изделия`);
          return of(undefined);
        }),
        tap(() => {
          const existModels = this.dataSource.data.filter(product =>
            this.selectedProducts.selected.includes(product.id)
          );

          this.update$.next();

          this.orderDetailService.getOrder();
          this.orderDetailService.getOrderEstimate();

          const parsedMaterials = existModels
            .filter(
              product =>
                product.loadingState === LoadingStateEnum.ProductMaterialReplaced ||
                product.loadingState === LoadingStateEnum.Completed ||
                product.loadingState === LoadingStateEnum.ProductMaterialLinked
            )
            .flatMap(model => model.parsedMaterials);

          parsedMaterials
            .filter(
              m => m.materialType === CadModelMaterialType.Plate || m.materialType === CadModelMaterialType.Linear
            )
            .forEach(m => {
              this.clientMaterialService.prepareForDeletion('product', m.linkedMaterialName, m.linkedMaterialArticle);
            });

          this.selectedProducts.clear();
          this.selectedAll = false;
          this.snackBar.open($localize`Изделия удалены`);
        })
      )
      .subscribe();
  }

  openLinkProductMaterials(products: CuttingProduct[]): void {
    this.linkProductMaterialsDialogRef = this.dialog.open(ProductMaterialsLinkDialogComponent, {
      disableClose: false,
      panelClass: 'cutting-product-material-link-dialog',
      width: '1000px',
    });

    const notLinked = products.filter(v => v.loadingState === LoadingStateEnum.ProductMaterialParsed);

    this.linkProductMaterialsDialogRef.componentInstance.products = notLinked;

    this.linkProductMaterialsDialogRef
      .afterClosed()
      .pipe(
        filter<CuttingProduct[]>(res => !_.isNil(res) && !_.isEmpty(res)),
        flatMap(res => of(...res)),
        concatMap(res => this.orderDetailService.saveProductLinkMaterials(res)),
        tap(() => {
          this.update$.next();
        }),
        finalize(() => (this.linkProductMaterialsDialogRef = null))
      )
      .subscribe();
  }

  showProductModel(product: CuttingProduct): void {
    if (product.fileName === '') {
      return;
    }

    const modelGuid = product.fileName.split('.').slice(0, -1).join('.');

    this.productModelViewerDialog = this.dialog.open(ProductModelViewerDialogComponent, {
      disableClose: false,
      panelClass: 'product-model-viewer-dialog',
      width: '95vw',
      height: '95vh',
      data: {
        modelUrl: ApiLinks.elasticProoductGlbModel(modelGuid),
      },
    });

    this.productModelViewerDialog
      .afterClosed()
      .pipe(finalize(() => (this.productModelViewerDialog = null)))
      .subscribe();
  }

  getProductImagePath(productId: number): string {
    return `${ApiLinks.elasticModelPriceList}/${this.orderId}/${productId}/cutting-preview`;
  }

  openEditProductDialog(product: CuttingProduct): void {
    const parentInjector = this.injector;

    const dialogRef = this.dialogsService.open(EditProductDialogComponent, {
      data: product,
      injector: Injector.create({
        providers: [{ provide: OrderDetailService, useValue: parentInjector.get(OrderDetailService) }],
        parent: parentInjector,
      }),
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(res => res),
        switchMap(() => {
          this.update$.next();
          this.orderDetailService.getOrder();
          this.orderDetailService.getOrderEstimate();

          return of(true);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private generateUniqueProductNumber(currentPositions: string[]): number {
    const nums: number[] = [];

    currentPositions.forEach(item => {
      const cleanItem = item.replace(/[^\d]/g, '');

      if (cleanItem !== '' && _.isNumber(+cleanItem)) {
        nums.push(+cleanItem);
      }
    });

    if (nums.length === 0) {
      return 1;
    }

    return Math.max(...nums) + 1;
  }
}
